import { Button } from "@pepperstone/pepper-ui-kit";
import { useMemo, useState } from "react";

import { Datepicker } from "../../../Datepicker";
import { DropdownOption, DropDownV2 } from "../../../DropDownV2";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { SearchInput } from "../../../SearchInput";
import { CLIENT_ANF_TABLE_FILTER_REPORT_START_DATE } from "../consts";
import { useClientApprovedNotFundedContext } from "../providers/ApprovedNotFundedProvider";
import { ClientANFTableFilterSubProps } from "./types";

export const DesktopFilters = ({
  handleDateRangePicker,
  handleExportButton,
  handleSearchInput,
  handleRebateAccountsDropdown,
  translations: { translatedContent, language },
  onDateTooltipHover,
}: ClientANFTableFilterSubProps) => {
  const isMobile = useIsMobile();
  const translatedLabels = useMemo(
    () => ({
      allItemsSelectedLabel:
        translatedContent.dropDownLabels?.allItemsSelectedLabel ?? "",
      applyButtonLabel:
        translatedContent.dropDownLabels?.applyButtonLabel ?? "",
      clearSelectionLabel:
        translatedContent.dropDownLabels?.clearSelectionLabel ?? "",
      itemsSelectedLabel:
        translatedContent.dropDownLabels?.itemsSelectedLabel ?? "",
      selectAccountLabel: translatedContent.dropDownLabels?.selectLabel ?? "",
      selectAllLabel: translatedContent.dropDownLabels?.selectAllLabel ?? "",
    }),
    [
      translatedContent.dropDownLabels?.allItemsSelectedLabel,
      translatedContent.dropDownLabels?.applyButtonLabel,
      translatedContent.dropDownLabels?.clearSelectionLabel,
      translatedContent.dropDownLabels?.itemsSelectedLabel,
      translatedContent.dropDownLabels?.selectAllLabel,
      translatedContent.dropDownLabels?.selectLabel,
    ],
  );

  const {
    state: {
      searchQuery,
      dateRange,
      selected: { rebateAccountIDs: selectedRebateAccountIDs },
    },
    options: { rebateAccountIDs },
  } = useClientApprovedNotFundedContext();

  const [search, setSearch] = useState(searchQuery);

  const selectedRebateAccounts = useMemo(() => {
    return ((selectedRebateAccountIDs as string[]) ?? []).map((id) => ({
      label:
        rebateAccountIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [rebateAccountIDs, selectedRebateAccountIDs]);

  const handleDropdownChange = (options: DropdownOption[]) => {
    handleRebateAccountsDropdown?.(options);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    handleSearchInput?.(value);
  };

  return (
    <div data-testid="desktop-filters" className="mt-10 flex flex-col">
      <div className="flex grow flex-row items-center gap-4">
        <div className="flex w-1/2 flex-row items-center justify-between gap-4">
          <div className="relative flex flex-grow basis-1/3">
            <DropDownV2
              id="searchDropdown"
              options={rebateAccountIDs}
              selected={selectedRebateAccounts}
              label={translatedContent.filters?.rebateAccounts}
              isMobile={isMobile}
              dropDownLabels={translatedLabels}
              handleApply={handleDropdownChange}
            />
          </div>
          <div className="flex flex-grow basis-2/3">
            <SearchInput
              label={translatedContent.searchLabel}
              value={search}
              onChange={handleSearchChange}
              placeholder={translatedContent?.filters?.searchNoPII}
            />
          </div>
        </div>

        <div className="flex w-3/5 flex-row items-center justify-between gap-4">
          <div className="flex min-w-64">
            <Datepicker
              value={{
                startDate: dateRange.from,
                endDate: dateRange.to,
              }}
              inputId="dateRangeFilter"
              onChange={handleDateRangePicker}
              configs={{
                shortcuts: {
                  today: translatedContent.today,
                  yesterday: translatedContent.yesterday,
                  past: (period) =>
                    translatedContent.past
                      ? translatedContent.past(period)
                      : "",
                  currentMonth: translatedContent.currentMonth,
                  pastMonth: translatedContent.pastMonth,
                },
                footer: {
                  cancel: translatedContent.cancel,
                  apply: translatedContent.apply,
                },
              }}
              i18n={language}
              label={
                translatedContent.approvedNotFundedTab?.filters?.approvalDate ||
                translatedContent.filters?.dates
              }
              allowClear={false}
              minDate={new Date(CLIENT_ANF_TABLE_FILTER_REPORT_START_DATE)}
              tooltip={
                translatedContent.filters
                  .approvedNotFundedDatePickerDisclaimerTooltip
              }
              onTooltipHover={onDateTooltipHover}
            />
          </div>
          <div className="flex h-[72px] w-1/3 flex-row items-end justify-end">
            <Button
              as="button"
              type="button"
              size="ps-btn-md"
              variant="ps-btn-secondary"
              onClick={handleExportButton}
            >
              {translatedContent.exportCSVText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
